import { useEffect, useRef, useState } from 'react';

import { type IVolunteerStatusResponse } from '../../../interface/IAgents';
import { type StatusMeta, statusMeta } from './types';

type StatusAction = (
	volunteerIds: number[],
	onSuccess?: (res: IVolunteerStatusResponse) => void,
	onFailure?: (err: any) => void
) => void;

const POLL_INTERVAL_MS = 30_000;

/**
 * Polls the anonymous POST /api/v1/volunteer-status endpoint for a single
 * application id and returns the derived status pill meta. Re-polls every 30s
 * while the page is visible. The action ref pattern keeps the effect from
 * re-subscribing on every parent render (the bound action is a new reference
 * each render) — same guard used across the app for DataWrapper actions.
 */
export const useVolunteerStatus = (id: number | undefined, action: StatusAction): StatusMeta | null => {
	const [status, setStatus] = useState<StatusMeta | null>(null);
	const actionRef = useRef(action);
	actionRef.current = action;

	useEffect(() => {
		if (!id) return;

		let cancelled = false;

		const poll = () => {
			actionRef.current([id], (res) => {
				if (cancelled) return;
				const item = res?.data?.find((entry) => entry.volunteerId === id) ?? res?.data?.[0];
				if (item && (item.statusId != null || item.status)) {
					setStatus(statusMeta(item.statusId, item.status));
				}
			});
		};

		poll();
		const timer = window.setInterval(poll, POLL_INTERVAL_MS);

		return () => {
			cancelled = true;
			window.clearInterval(timer);
		};
	}, [id]);

	return status;
};
